import {IconButton, Link} from '@wandb/components';
import {A} from '@wandb/weave/common/util/links';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useState} from 'react';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon as SemanticIcon, Message, Modal} from 'semantic-ui-react';

import DeleteRunModal from '../../components/DeleteRunModal';
import {useFetchRunJob} from '../../components/Launch/hooks';
import {JobDrawer} from '../../components/Launch/JobDrawer/JobDrawer';
import {useShouldShowLaunchButton} from '../../components/Launch/launchActive';
import {RunCopyIdentifiersSubmenu} from '../../components/RunSelector/RowHeaderMenu/RunCopyIdentifiersSubmenu';
import {useRunOverflowMenuQuery} from '../../generated/graphql';
import * as GQLTypes from '../../types/graphql';
import DOC_URLS from '../../util/doc_urls';
import {useStatsigGateWorkspaceConsolidation} from '../../util/rampFeatureFlags';
import * as RunHelpers from '../../util/runhelpers';
import * as urls from '../../util/urls';
import {RenameRunDialog} from './RenameRunDialog';

type RunOverflowMenuProps = {
  // props from RunPage2, remove after workspace consolidation is fully rolled out
  run?: GQLTypes.RunPageRunQuery;
  project?: GQLTypes.Project;
  readOnly?: boolean;

  customRunName: string | undefined;
  entityName: string;
  projectName: string;
  runName: string;
};
export const RunOverflowMenu = ({
  run: runFromProps,
  project: projectFromProps,
  readOnly: readOnlyFromProps,
  customRunName,
  entityName,
  projectName,
  runName,
}: RunOverflowMenuProps) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isExportDataModalOpen, setIsExportDataModalOpen] = useState(false);
  const [isViewCodeModalOpen, setIsViewCodeModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const runData = useRunOverflowMenuQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
  });

  const run = isWorkspaceConsolidationEnabled
    ? runData.data?.project?.run
    : runFromProps;

  const project = isWorkspaceConsolidationEnabled
    ? runData.data?.project
    : projectFromProps;

  const readOnly = isWorkspaceConsolidationEnabled
    ? runData.data?.project?.readOnly
    : readOnlyFromProps;

  // Get the job that created this run (if any)
  const {jobInfo} = useFetchRunJob({
    entityName,
    projectName,
    runName: run?.name ?? '',
    skip: run?.inputArtifacts == null || run?.inputArtifacts.totalCount === 0,
  });
  const {shouldShow: shouldShowRerunButton} =
    useShouldShowLaunchButton(jobInfo);
  const [jobToRun, setJobToRun] = useState<string | null>(null);
  const closeDrawer = () => {
    setJobToRun(null);
  };

  if (!run || !project) {
    return null;
  }

  const cloneRun = (jobId: string) => {
    setJobToRun(jobId);
  };

  const jobId = jobInfo?.id;

  const runUrl = urls.run({
    entityName,
    projectName,
    name: runName,
  });
  const createRunCodeSnippet = `import wandb
    api = wandb.Api()
    run = api.run("${runUrl}")`;

  const hasGitHub = run.commit && run.github;
  const codeIcon = hasGitHub ? 'github' : 'code-alt';

  return (
    <>
      {isRenameDialogOpen && (
        <RenameRunDialog
          runId={run.id}
          runName={run.name}
          runDisplayName={run.displayName ?? ''}
          closeDialog={() => setIsRenameDialogOpen(false)}
        />
      )}
      <JobDrawer
        open={jobToRun != null}
        closeDrawer={closeDrawer}
        entityName={project.entityName}
        projectName={project.name}
        defaultJobId={jobToRun ?? ''}
        defaultJobVersion={jobInfo?.versionStr ?? ''}
        defaultRunToClone={run.name}
      />
      <Modal
        open={isExportDataModalOpen}
        onClose={() => setIsExportDataModalOpen(false)}>
        <Modal.Header>
          <h1>Python API</h1>
        </Modal.Header>
        <Modal.Content>
          <h4>Create the run object:</h4>
          <pre className="language-python" style={{padding: '0em'}}>
            {createRunCodeSnippet}
          </pre>
          <h4>Show history data:</h4>
          <pre className="language-python">{`print(run.history())`}</pre>
          <p style={{marginTop: 20}}>
            Check out the{' '}
            <Link href={DOC_URLS.publicApi} target="_blank">
              API docs
            </Link>
            {' for more information.'}
          </p>
        </Modal.Content>
      </Modal>
      <Modal
        open={isViewCodeModalOpen}
        onClose={() => setIsViewCodeModalOpen(false)}
        on="click">
        <Modal.Header>
          <h1>View Code</h1>
        </Modal.Header>
        <Modal.Content>
          <Message icon>
            <SemanticIcon name={hasGitHub ? 'github' : 'code'} />
            <Message.Content>
              <p>
                Wandb will automatically save the git commit ID of the last
                commit before each of your runs.
              </p>
              {hasGitHub ? (
                <>
                  <p>
                    You can find your commit{' '}
                    <Link href={run.github ?? ''} target="_blank">
                      here on GitHub
                    </Link>
                    .
                  </p>
                </>
              ) : run.runInfo && run.runInfo.git ? (
                <table className="run-page-table">
                  <tr>
                    <td className="key">
                      <span className="overview-key">Git remote</span>
                    </td>
                    <td className="command">{run.runInfo.git.remote ?? '-'}</td>
                  </tr>
                  <tr>
                    <td className="key">
                      <span className="overview-key">Git commit</span>
                    </td>
                    <td className="command">{run.runInfo.git.commit ?? '-'}</td>
                  </tr>
                </table>
              ) : (
                <p>
                  However, we couldn't find a git commit for this particular
                  run.
                </p>
              )}
              {RunHelpers.hasDiffPatchFile(run.diffPatchFile) && (
                <p>
                  Your uncommitted changes were saved in file called{' '}
                  <strong>diff.patch</strong>, which you can download{' '}
                  <A
                    href={urls.runDiffPatchFileUrl({
                      entityName,
                      projectName,
                      runName,
                    })}
                    download="diff.patch">
                    here
                  </A>
                </p>
              )}
            </Message.Content>
          </Message>
        </Modal.Content>
      </Modal>
      <DeleteRunModal
        closeModal={() => setDeleteModalOpen(false)}
        isOpen={isDeleteModalOpen}
        setIsModalOpen={setDeleteModalOpen}
        entityName={entityName}
        isRunActive={run.state === 'running'}
        projectName={projectName}
        runDisplayName={run.displayName ?? ''}
        runId={run.id ?? ''}
        runName={run.name ?? ''}
      />
      <DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenu.Trigger>
          <IconButton
            isActive={isDropdownOpen}
            icon="overflow-horizontal"
            variant="secondary"
            aria-label="Open run overflow menu"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" className="min-w-[200px]">
            {!readOnly && isWorkspaceConsolidationEnabled && (
              <DropdownMenu.Item onClick={() => setIsRenameDialogOpen(true)}>
                <Icon name="pencil-edit" />
                Edit run name
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
              onClick={() => {
                setIsExportDataModalOpen(true);
              }}>
              <Icon name="database-artifacts" />
              Export data
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setIsViewCodeModalOpen(true)}>
              <Icon name={codeIcon} />
              View code
            </DropdownMenu.Item>
            <RunCopyIdentifiersSubmenu
              onCopyRunName={() => {
                copyToClipboard(customRunName ?? run.displayName ?? run.name);
                toast('Copied run name to clipboard');
                window.analytics?.track('Run name copied to clipboard');
              }}
              onCopyRunPath={() => {
                copyToClipboard(`${entityName}/${projectName}/${run.name}`);
              }}
            />
            {shouldShowRerunButton && jobId && (
              <DropdownMenu.Item onClick={() => cloneRun(jobId)}>
                <>
                  <Icon name="play" variant="ghost" />
                  Rerun with Launch
                </>
              </DropdownMenu.Item>
            )}
            {!readOnly && (
              <>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  content="Delete run"
                  onClick={() => setDeleteModalOpen(true)}>
                  <Icon name="delete" />
                  Delete run
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
};
